import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import NumberInput from "@/components/base/NumberInput";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import type { Client, InventoryItem, Product, Receivable } from "@/types";
import { formatCOP, formatNumber } from "@/utils/format";

interface ReceivableFormModalProps {
  open: boolean;
  clients?: Client[];
  products?: Product[];
  inventory?: InventoryItem[];
  defaultClientName?: string;
  onClose: () => void;
  onSubmit: (r: Receivable) => void;
}

// Stock total (todos los frentes) para saber qué hay realmente en inventario.
const totalStock = (inv: InventoryItem): number =>
  (inv.stockCasa ?? 0) +
  (inv.stockPuntoClear ?? 0) +
  Object.values(inv.stockByAdvisor ?? {}).reduce((s, n) => s + n, 0) +
  Object.values(inv.stockOwnedByAdvisor ?? {}).reduce((s, n) => s + n, 0);

const ReceivableFormModal = ({
  open,
  clients = [],
  products = [],
  inventory = [],
  defaultClientName = "",
  onClose,
  onSubmit,
}: ReceivableFormModalProps) => {
  const [clientId, setClientId] = useState("");
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState(1);
  const [initialValue, setInitialValue] = useState(0);
  const [firstPayment, setFirstPayment] = useState(0);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState("");

  // Solo productos con inventario disponible (no se puede fiar lo que no hay).
  const availableProducts = useMemo(() => {
    const list = Array.isArray(products) ? products : [];
    const invs = Array.isArray(inventory) ? inventory : [];
    return list.filter((p) => {
      const inv = invs.find((i) => i.productId === p.id);
      return inv ? totalStock(inv) > 0 : false;
    });
  }, [products, inventory]);

  // Stock disponible del producto elegido (para topar la cantidad fiada).
  const selectedStock = useMemo(() => {
    const inv = inventory.find((i) => i.productId === productId);
    return inv ? totalStock(inv) : 0;
  }, [inventory, productId]);

  useEffect(() => {
    if (!open) return;
    // Si venimos desde la ficha de un cliente, lo preseleccionamos por nombre.
    const preset = defaultClientName
      ? clients.find(
          (c) => c.name.trim().toLowerCase() === defaultClientName.trim().toLowerCase()
        )
      : undefined;
    setClientId(preset ? preset.id : "");
    setProductId("");
    setQty(1);
    setInitialValue(0);
    setFirstPayment(0);
    setDate(new Date().toISOString().slice(0, 10));
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // El valor inicial se sugiere desde el precio público del producto por la cantidad.
  useEffect(() => {
    if (!open) return;
    const product = products.find((p) => p.id === productId);
    if (product) setInitialValue(product.pricePublic * (Number(qty) || 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productId, qty]);

  const submit = () => {
    const client = clients.find((c) => c.id === clientId);
    if (!client) {
      setError("Elige el cliente del fiado.");
      return;
    }
    const product = products.find((p) => p.id === productId);
    if (!product) {
      setError("Elige el producto del catálogo.");
      return;
    }
    const q = Number(qty) || 0;
    if (q <= 0) {
      setError("La cantidad debe ser mayor a cero.");
      return;
    }
    if (q > selectedStock) {
      setError(
        `Solo hay ${formatNumber(selectedStock)} unidades disponibles de este producto. No puedes fiar más de lo que tienes en inventario.`
      );
      return;
    }
    if (Number(initialValue) <= 0) {
      setError("El valor inicial debe ser mayor a cero.");
      return;
    }
    const payment = Number(firstPayment) || 0;
    if (payment > Number(initialValue)) {
      setError("El abono inicial no puede superar el valor total.");
      return;
    }
    const isoDate = new Date(date).toISOString();
    const r: Receivable = {
      id: "",
      date: isoDate,
      clientName: client.name.trim(),
      productName: `${product.name}${(Number(qty) || 1) > 1 ? ` x${qty}` : ""}`,
      initialValue: Number(initialValue) || 0,
      payments: payment > 0 ? [{ amount: payment, date: isoDate }] : [],
    };
    onSubmit(r);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nuevo fiado a cliente"
      icon="ri-file-list-3-line"
      size="lg"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={submit} className={primaryButtonClass}>
            <i className="ri-save-3-line" /> Registrar fiado
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className={labelClass}>Cliente (ya registrado)</label>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputClass}>
            <option value="" className="bg-background-100">
              — selecciona un cliente —
            </option>
            {clients.map((c) => (
              <option key={c.id} value={c.id} className="bg-background-100">
                {c.name}
                {c.cedula ? ` · ${c.cedula}` : ""}
              </option>
            ))}
          </select>
          {clients.length === 0 && (
            <p className="text-[10px] text-primary-200 mt-1 flex items-center gap-1.5">
              <i className="ri-alert-line" /> No hay clientes registrados. Créalos primero en Clientes.
            </p>
          )}
          {availableProducts.length === 0 && (
            <p className="text-[10px] text-primary-200 mt-1 flex items-center gap-1.5">
              <i className="ri-alert-line" /> No hay productos con stock. Ingresa inventario antes de
              fiar.
            </p>
          )}
        </div>

        <div className="sm:col-span-2">
          <label className={labelClass}>Producto (de tu catálogo con inventario)</label>
          <select value={productId} onChange={(e) => setProductId(e.target.value)} className={inputClass}>
            <option value="" className="bg-background-100">
              — selecciona un producto —
            </option>
            {availableProducts.map((p) => {
              const inv = inventory.find((i) => i.productId === p.id);
              const stock = inv ? totalStock(inv) : 0;
              return (
                <option key={p.id} value={p.id} className="bg-background-100">
                  {p.name} · {formatCOP(p.pricePublic)}
                  {stock > 0 ? ` · ${formatNumber(stock)} und` : ""}
                </option>
              );
            })}
          </select>
        </div>

        <div>
          <label className={labelClass}>Cantidad</label>
          <NumberInput min={1} value={qty} onChange={setQty} className={inputClass} />
          {productId && (
            <p className="text-[10px] text-foreground-500 mt-1">
              Disponible: {formatNumber(selectedStock)} und
            </p>
          )}
        </div>
        <div>
          <label className={labelClass}>Valor inicial (COP)</label>
          <NumberInput min={0} value={initialValue} onChange={setInitialValue} className={inputClass} />
          <p className="text-[10px] text-foreground-500 mt-1">{formatCOP(Number(initialValue) || 0)}</p>
        </div>
        <div>
          <label className={labelClass}>Abono inicial (opcional)</label>
          <NumberInput min={0} value={firstPayment} onChange={setFirstPayment} className={inputClass} />
          <p className="text-[10px] text-foreground-500 mt-1">{formatCOP(Number(firstPayment) || 0)}</p>
        </div>
        <div>
          <label className={labelClass}>Fecha</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}
    </Modal>
  );
};

export default ReceivableFormModal;